import type { MutationStatus } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import { signalsQueryKeys } from "app/pages/sales-intelligence/sub-modules/signals/query/key-factory";
import { useCreateSignalMutationState } from "app/pages/sales-intelligence/sub-modules/signals/query/mutations/useCreateSignalMutationState";
import { useSaveDraftSignalMutationState } from "app/pages/sales-intelligence/sub-modules/signals/query/mutations/useSaveDraftSignalMutationState";

type HookValue = {
    signal: Signal | null;
    createStatus?: MutationStatus;
    saveStatus?: MutationStatus;
    onSaveSuccess: VoidFunction;
};

export const useSignalDraftForMessage = (messageId: string): HookValue => {
    const queryClient = useQueryClient();

    const createSignalState = useCreateSignalMutationState(messageId);
    const saveDraftState = useSaveDraftSignalMutationState(messageId);

    const getActiveSignal = (): Signal | null => {
        if (saveDraftState?.status === "success" && saveDraftState.data) {
            return saveDraftState.data;
        }

        if (createSignalState?.status === "success" && createSignalState.data?.signal) {
            return createSignalState.data.signal;
        }

        return null;
    };

    const onSaveSuccess = (): void => {
        void queryClient.resetQueries({
            queryKey: signalsQueryKeys.createSignal(messageId),
            exact: true,
        });
    };

    return {
        signal: getActiveSignal(),
        createStatus: createSignalState?.status,
        saveStatus: saveDraftState?.status,
        onSaveSuccess,
    };
};
